import { FC, ReactNode, Children, isValidElement } from 'react';
import { makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import { useWbTabsContext } from './WbTabsContext';
import { WbTabItemProps } from './WbTabItem';
import clsx from 'clsx';

interface WbTabsProps {
  children: ReactNode;
  'aria-label': string;
  className?: string;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    defaultTab: {
      color: theme.palette.text.primary,
      opacity: 1,
      fontWeight: 500,
      margin: '0 8px',
      minWidth: 0,
      padding: 0,
      letterSpacing: '0.009rem',
      fontSize: '1.125rem',
      '&:first-child': {
        marginLeft: 0,
      },
    },
    tabRoot: {
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main,
      },
    },
    selected: {
      color: theme.palette.primary.main,
    },
  }),
  { name: 'WbTabs' },
);

export const WbTabs: FC<WbTabsProps> = ({
  children,
  'aria-label': ariaLabel,
  className,
}) => {
  const classes = useStyles();
  const { value, onValueChange, getPanelId, getTabId } = useWbTabsContext();
  return (
    <Tabs
      selectionFollowsFocus
      indicatorColor="primary"
      aria-label={ariaLabel}
      onChange={(_e, val) => {
        onValueChange(val);
      }}
      value={value}
      variant="scrollable"
      scrollButtons="auto"
      className={className}
    >
      {Children.toArray(children).map(child => {
        if (!isValidElement<WbTabItemProps>(child)) return null;
        const props = child.props;
        return (
          <Tab
            disabled={props.disabled}
            label={props.title}
            key={props.value}
            value={props.value}
            id={getTabId(props.value)}
            aria-controls={getPanelId(props.value)}
            className={clsx(className, classes.defaultTab)}
            classes={{ selected: classes.selected, root: classes.tabRoot }}
          />
        );
      })}
    </Tabs>
  );
};
